/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useMemo } from 'react'
import * as THREE from 'three'
import { useFrame } from '@react-three/fiber'
import { frame } from '../flight/state.ts'
import { makeGlyphAtlas } from '../assets/atlas.ts'
import { loadTexture } from '../assets/loaders.ts'
import { GLYPH_PATHS } from './tubesData.ts'
import { DESKS } from '../world.ts'
import { HASH } from '../shaders/chunks.ts'
import { mulberry32 } from '../prng.ts'
import { Q } from '../quality.ts'
import { glsl, INK, CYAN, MAGENTA, YELLOW } from '../palette.ts'

// FOUNDATION §6.3 — the far glyph field. Every glyph lives a desk-anchored life:
// born rising from ITS monitor (columns, not volume static), a ~38% subpopulation is
// captured into the tubes at MIX (the rest stay free mist until the snap), and every
// glyph's ordered destination is a rank aligned to the desk rows — order arrives where
// the work happens. All positions are pure f(uniforms, time, seed): stateless, exact
// in reverse (SPEC §2).

const VERT = /* glsl */ `
attribute float aSeed;
attribute float aIndex;
attribute float aBirth;
attribute vec3 aHome;   // this glyph's monitor
attribute vec3 aRank;   // this glyph's ordered destination (desk-row aligned)
uniform float uTime, uFlow, uActive, uReveal, uSpread, uCalm, uChroma, uFog, uPool;
uniform vec3 uBg;
uniform vec3 uHero;     // the opening ribbon's origin (the cutout's desk)
uniform vec3 uB0[8]; uniform vec3 uB1[8]; uniform vec3 uB2[8]; uniform vec3 uB3[8];
varying vec2 vUv;
varying vec3 vCol;
${HASH}
void main() {
  vec3 r = hash31(aSeed);
  vec3 r2 = hash31(aSeed + 17.17);
  float cellI = floor(r2.z * 63.999);
  vUv = (vec2(mod(cellI, 8.0), floor(cellI / 8.0)) + uv) / 8.0;

  // ---- mode 0: turbulent — a rising column from this glyph's own monitor
  vec3 base = mix(uHero, aHome, clamp(uSpread, 0.0, 1.0));
  float speed = 0.5 + r2.x * 0.9;
  float h = mix(10.0, 15.0, uSpread) * uCalm;
  float y0 = mod(r.y * h + uTime * speed, h);
  float sw = (0.13 + y0 * 0.11) * (0.45 + 0.55 * uSpread);
  vec3 p0 = vec3(
    base.x + sin(y0 * 0.55 + aSeed * 6.283) * sw,
    base.y + y0,
    base.z + cos(y0 * 0.47 + aSeed * 4.71) * sw
  );
  // the hero ribbon branches as it rises (HOLD reference)
  p0.x += step(3.0, y0) * (r2.y - 0.5) * (y0 - 3.0) * 0.3 * (1.0 - uSpread);

  // ---- mode 1: constrained — only the captured subpopulation enters the tubes
  float cap = step(0.62, hash11(aSeed + 7.7));
  int k = int(mod(floor(aSeed * 1000.0), 8.0));
  float tt = fract(r.y + uTime * (0.10 + 0.14 * r2.x));
  float it = 1.0 - tt;
  vec3 p1 = it*it*it*uB0[k] + 3.0*it*it*tt*uB1[k] + 3.0*it*tt*tt*uB2[k] + tt*tt*tt*uB3[k];
  p1 += (hash31(aSeed + 3.3) - 0.5) * 0.22;

  // ---- mode 2: ordered ranks over the desk rows, settled but alive
  vec3 p2 = aRank;
  p2.y += 0.03 * sin(uTime * 1.8 + aRank.x * 1.4 + aRank.z * 0.8);

  // ---- mode 3: dissolve — streams gathered around the corridor exit and the
  // rising camera, thinning upward past it
  float y3 = mod(r.y * 90.0 + uTime * (2.0 + 2.5 * r2.x), 90.0);
  vec3 p3 = vec3((r.x * 2.0 - 1.0) * 45.0, 2.0 + y3 * 1.7, 40.0 + r.z * 130.0);
  float fade3 = 1.0 - smoothstep(105.0, 152.0, 2.0 + y3 * 1.7);

  // staggered mode transitions ripple through the field instead of snapping at once
  // windows saturate 0.2 before the integer so the stagger never leaves a residual
  // fraction of the previous mode's position — a 5% residual of a 30m journey is
  // 1.5m of scatter, which is the difference between ranks and confetti
  float fs = uFlow + (r2.y - 0.5) * 0.35;
  float m1 = smoothstep(0.05, 0.8, fs) * cap;
  float m2 = smoothstep(1.05, 1.8, fs);
  float m3 = smoothstep(2.05, 2.8, fs);
  vec3 pos = mix(mix(mix(p0, p1, m1), p2, m2), p3, m3);

  // a glyph exists once its desk exists (CREATE grows outward from the one),
  // and is consumed into sheets at ARTIFACTS (uActive falls)
  float act = step(aBirth, uReveal) * step(aIndex / uPool, uActive);
  float gridness = m2 * (1.0 - m3);
  float size = (0.15 + r2.x * 0.18) * act * (1.0 + 0.35 * gridness + 0.6 * m3);

  float ang = (uTime * (0.6 + r.x * 1.2) + aSeed * 6.283) * (1.0 - m2 * 0.95);
  float ca = cos(ang), sa = sin(ang);
  vec2 pv = vec2(position.x * ca - position.y * sa, position.x * sa + position.y * ca);

  vec4 mv = modelViewMatrix * vec4(pos, 1.0);
  mv.xy += pv * size;
  gl_Position = projectionMatrix * mv;

  float dist = length(mv.xyz) * (1.0 - 0.45 * m3); // the last streams stay ink against the sky
  vec3 ink = ${glsl(INK)};
  // cyan = information being routed or ordered (law 1); free chaos stays ink.
  // A sparse few carry the other randommonkey.io ink dots — magenta and yellow
  // highlights threaded through the streams and ranks
  vec3 accent = ${glsl(CYAN)};
  float ha = hash11(aSeed + 31.7);
  if (ha > 0.966) accent = ${glsl(YELLOW)};
  else if (ha > 0.925) accent = ${glsl(MAGENTA)};
  float info = max(gridness, m1 * 0.8) * uChroma;
  vec3 col = mix(ink, accent, info * 0.85);
  float fo = 1.0 - exp(-uFog * uFog * dist * dist);
  // aerial desaturation collapses contrast with distance — but information keeps
  // its colour longer than the air keeps its ink, or peak chroma dies at the god view
  float lum = dot(col, vec3(0.299, 0.587, 0.114));
  float desat = clamp(fo * 1.7, 0.0, 1.0) * (1.0 - 0.75 * info);
  col = mix(col, vec3(lum), desat);
  col = mix(col, uBg, fo);
  float vis = act * mix(1.0, fade3, m3);
  vCol = mix(uBg, col, vis);
}
`

const FRAG = /* glsl */ `
uniform sampler2D uAtlas;
varying vec2 vUv;
varying vec3 vCol;
void main() {
  float a = texture2D(uAtlas, vUv).a;
  if (a < 0.4) discard;
  gl_FragColor = vec4(vCol, 1.0);
}
`

export function Glyphs() {
  const { geometry, material } = useMemo(() => {
    const N = Q.glyphs
    const base = new THREE.PlaneGeometry(1, 1)
    const geometry = new THREE.InstancedBufferGeometry()
    geometry.index = base.index
    geometry.setAttribute('position', base.attributes.position)
    geometry.setAttribute('uv', base.attributes.uv)
    const rand = mulberry32(4242)
    const seeds = new Float32Array(N)
    const idx = new Float32Array(N)
    const births = new Float32Array(N)
    const homes = new Float32Array(N * 3)
    const ranks = new Float32Array(N * 3)
    const field = DESKS.slice(1)
    // ranks: row-carpets locked to the desk grid pitch (SZ = 2.4) — dense in x,
    // desk-pitch gaps in z, only ~2 layers deep so the rows read as lines from above
    const ROWS = 57
    const COLS = 320
    for (let i = 0; i < N; i++) {
      seeds[i] = rand() * 120
      idx[i] = i
      const d = field[Math.floor(rand() * field.length)]
      // ribbon glyphs (first ~600) exist before any desk is revealed
      births[i] = i < 600 ? -1 : d.birth
      homes[i * 3] = d.x - Math.sin(d.yaw) * 0.05
      homes[i * 3 + 1] = 1.2
      homes[i * 3 + 2] = d.z - Math.cos(d.yaw) * 0.18
      const row = i % ROWS
      const col = Math.floor(i / ROWS) % COLS
      const layer = Math.floor(i / (ROWS * COLS))
      ranks[i * 3] = -54.4 + col * 0.34
      ranks[i * 3 + 1] = 1.5 + layer * 0.3
      ranks[i * 3 + 2] = -57.6 + row * 2.4
    }
    geometry.setAttribute('aSeed', new THREE.InstancedBufferAttribute(seeds, 1))
    geometry.setAttribute('aIndex', new THREE.InstancedBufferAttribute(idx, 1))
    geometry.setAttribute('aBirth', new THREE.InstancedBufferAttribute(births, 1))
    geometry.setAttribute('aHome', new THREE.InstancedBufferAttribute(homes, 3))
    geometry.setAttribute('aRank', new THREE.InstancedBufferAttribute(ranks, 3))
    geometry.instanceCount = N

    const material = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: {
        uAtlas: { value: loadTexture('/assets/glyphs.png', makeGlyphAtlas) },
        uTime: { value: 0 },
        uFlow: { value: 0 },
        uActive: { value: 0.012 },
        uReveal: { value: 0 },
        uSpread: { value: 0 },
        uCalm: { value: 1 },
        uChroma: { value: 0 },
        uFog: { value: 0.016 },
        uPool: { value: N },
        uBg: { value: new THREE.Color() },
        // the opening ribbon rises from the hero's typewriter (HeroApe.tsx DESK)
        uHero: { value: new THREE.Vector3(2.64, 0.85, 0.57) },
        uB0: { value: GLYPH_PATHS.map((p) => p[0]) },
        uB1: { value: GLYPH_PATHS.map((p) => p[1]) },
        uB2: { value: GLYPH_PATHS.map((p) => p[2]) },
        uB3: { value: GLYPH_PATHS.map((p) => p[3]) },
      },
    })
    return { geometry, material }
  }, [])

  useFrame((st) => {
    const b = frame.beats
    const u = material.uniforms
    u.uTime.value = st.clock.elapsedTime
    u.uFlow.value = b.flow
    u.uActive.value = b.active
    u.uReveal.value = b.reveal
    u.uSpread.value = b.spread
    u.uCalm.value = b.calm
    u.uChroma.value = b.chroma
    u.uFog.value = b.fog
    ;(u.uBg.value as THREE.Color).copy(frame.bg)
  })

  return <mesh geometry={geometry} material={material} frustumCulled={false} />
}
